import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChildren,
  QueryList,
  EventEmitter,
  OnDestroy,
  OnInit
} from '@angular/core';
import { ItemHotelsComponent } from '../item-hotels/item-hotels.component';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { switchMap, map, finalize } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  HotelAvalibilityQuoteResult,
  HotelEnterpriseSearchInterface,
  HotelQuoteResult,
  LocationTypes,
  HotelViewController,
  HotelView,
  IHotelResultsConfiguration,
  SortType,
  daysBetween,
  HotelDetails
} from '@sabstravtech/obtservices/base';
import { LightningUserFavorurite } from '../../../../core/classes/user-favourite.enum';
import {
  EnterpriseSearchService,
  Environment,
  HotelAvalibilityService,
  HotelItinerary,
  ServiceType,
  UserService,
  HotelRatingIdentifiers,
  GetMultipleHotelRatingResponse,
  HotelRatingInfoAndTags,
  HotelGroupStaticTags,
  ResultAwareService,
  HotelOriginalResultsFetcher
} from '@sabstravtech/obtservices/angular';

import { BaseResultsComponent } from '../../../../core/components/base_components/base-results.component';
import { DeviceDetector } from '../../../../core/services/device-detector.service';

import { UnitType } from '../../../../core/interfaces/hotel-search-form-configuration';
import { ThemeService } from '../../../../core/services/theme.service';

@Component({
  selector: 'app-hotels',
  templateUrl: './hotels.component.html',
  styleUrls: ['./hotels.component.scss'],
  standalone: false
})
export class HotelsComponent
  extends BaseResultsComponent
  implements AfterViewInit, OnDestroy, OnInit
{
  public readonly moreInfo = 'hotels_moreinfo';

  @ViewChildren(ItemHotelsComponent, { read: ElementRef })
  public hotels: QueryList<ElementRef>;
  public arrayOfKeys: string[];
  public noOfRooms = 1;
  public show_errors: boolean;
  public showFilter = false;
  public filteredSearchResults: HotelItinerary[] = [];
  public hotelViewController: HotelViewController;
  public showPreferredMessage = '';
  public activeLocation: LocationTypes;

  private delayValue = 200; // in ms
  private readyToClose = false;
  private updatedHotelIds: string[] = [];
  private jitFilterSub: Subscription | null = null;
  public searchParams: HotelEnterpriseSearchInterface;
  public originalResultsNotFound$: Observable<boolean>;
  public loadingOriginalResults = false;
  public availabilityFilterState = false;
  public viewPortSize = new BehaviorSubject<number>(0);
  public currentResults = new BehaviorSubject<HotelDetails[]>([]);
  public secondTabResults = new BehaviorSubject<HotelDetails[]>([]);
  public loadingScreenInview = new BehaviorSubject<boolean>(false);
  public view: HotelView = HotelView.LIST;
  // Needs to delete
  secondTabSelected = false;

  public hotelAvalibility: {
    [key: number]: BehaviorSubject<HotelAvalibilityQuoteResult>;
  } = {};
  public hotelRatings: {
    [key: number]: BehaviorSubject<HotelRatingInfoAndTags>;
  } = {};
  public hotelGroupTags: {
    [key: number]: BehaviorSubject<HotelGroupStaticTags>;
  } = {};

  public maxValue = 0;
  public co2Number = 0;

  ServiceType: typeof ServiceType = ServiceType;
  LocationTypes: typeof LocationTypes = LocationTypes;
  userDistanceUnit: string;
  userLocalCurrency: string;
  sortType: SortType;
  HotelView = HotelView;
  swapSellHotelGroup: string[] = null;

  /**
   * Array of methods and titles for SortComponent
   *
   * @type {SortType[]}
   * @memberof HotelListComponent
   */
  sortTypes: SortType[] = this.themeService.isScionEnv
    ? [
        {
          methodRef: () => this.searchService.searches[ServiceType.Hotel].byPreferred(),
          title: this.translateService.instant('Preferred Hotels'),
          icon: 'ico-heart'
        },
        {
          methodRef: () => this.searchService.searches[ServiceType.Hotel].sortByDistanceScion(),
          title: this.translateService.instant('Closest'),
          icon: 'ico-bed'
        },
        {
          methodRef: () => this.searchService.searches[ServiceType.Hotel].sortByDistanceReverse(),
          title: this.translateService.instant('Furthest'),
          icon: 'ico-bed'
        },
        {
          methodRef: () => this.searchService.searches[ServiceType.Hotel].sortByName(),
          title: this.translateService.instant('Name'),
          icon: 'ico-hotel'
        },
        {
          methodRef: () => this.searchService.searches[ServiceType.Hotel].sortByPrice(),
          title: this.translateService.instant('Price'),
          icon: 'ico-gbp'
        }
      ]
    : [
        {
          methodRef: () => this.searchService.searches[ServiceType.Hotel].byPreferred(),
          title: this.translateService.instant('Preferred Hotels'),
          icon: 'ico-heart'
        },
        {
          methodRef: () => this.searchService.searches[ServiceType.Hotel].sortByDistance(),
          title: this.translateService.instant('Distance'),
          icon: 'ico-bed'
        },
        {
          methodRef: () => this.searchService.searches[ServiceType.Hotel].sortByName(),
          title: this.translateService.instant('Name'),
          icon: 'ico-hotel'
        },
        {
          methodRef: () => this.searchService.searches[ServiceType.Hotel].sortByPrice(),
          title: this.translateService.instant('Price'),
          icon: 'ico-gbp'
        }
      ];
  maxResultSize: number;
  loadingHotelRatings = false;

  constructor(
    public searchService: EnterpriseSearchService,
    public deviceDetector: DeviceDetector,
    private userService: UserService,
    public hotelAvalibilityService: HotelAvalibilityService,
    private resultAwareService: ResultAwareService,
    private hotelOriginalResultsFetcher: HotelOriginalResultsFetcher,
    title: Title,
    public translateService: TranslateService,
    private environment: Environment,
    protected themeService: ThemeService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    super(title, translateService, themeService, searchService, userService, router);
    this.searchParams = searchService.searches[ServiceType.Hotel];
    this.originalResultsNotFound$ = this.searchParams.originalResultsNotFound;
    this.searchParams.byPreferred();
  }

  ngOnInit(): void {
    const resultsId = this.activatedRoute.snapshot.queryParamMap.get('results');
    if (resultsId) {
      this.loadingOriginalResults = true;
      this.searchParams
        .loadOriginalResults(resultsId, this.hotelOriginalResultsFetcher)
        .finally(() => {
          this.loadingOriginalResults = false;
        });
    }

    const currencyObject = this.userService.getUserFavoriteObject<{
      currency: string;
      hotel: { enable: boolean };
    }>(LightningUserFavorurite.PreferredCurrency);

    this.userLocalCurrency =
      currencyObject?.hotel?.enable && currencyObject?.currency ? currencyObject.currency : 'GBP';
    const hotelDistanceUnitConfig: any = this.userService.getUserFavoriteObject(
      LightningUserFavorurite.distanceUnit
    );
    this.userDistanceUnit = hotelDistanceUnitConfig?.unit || UnitType.Miles;
    this.noOfRooms = this.searchParams.no_of_rooms;
    this.hotelAvalibility = {};
    const userFavoriteAvailability = this.userService.isUserFavoriteSet(
      LightningUserFavorurite.hotelFilterDefaultAvailability
    );
    this.hotelViewController = {
      showMap: false,
      ratesUpdated: new EventEmitter(false),
      filterChange: new EventEmitter<HotelQuoteResult[]>(),
      hotelChosen: false,
      filtered: false,
      showFilterOptions: false,
      selectedVals: [],
      nr: 0,
      hotelCodeChosen: null,
      single: false,
      closeFilterOptions: () => {
        this.closeFilterOptions();
      }
    };

    this.subscribe(this.searchParams.selectItemMapView, (hotel: any) => {
      this.hotelViewController.hotelChosen = hotel;
      this.hotelViewController.hotelCodeChosen = hotel.id;
      this.hotelViewController.showMap = true;
      this.hotelViewController.single = true;
    });
    this.viewPortSize.next(this.environment.numberHotelToPreload);
    this.subscribe(
      combineLatest([this.searchParams.results, this.viewPortSize]).pipe(
        switchMap(([results, size]: [HotelDetails[], number]) => {
          this.co2Number =
            results[0]?.co2PerItem /
            daysBetween(this.searchParams.checkin_date, this.searchParams.checkout_date);
          this.co2Number = Number(this.co2Number.toFixed(2));
          this.currentResults.next(
            results.filter((hotel: HotelDetails) => !hotel.secondTab).slice(0, size)
          );
          this.secondTabResults.next(
            results.filter((hotel: HotelDetails) => hotel.secondTab).slice(0, size)
          );
          if (!this.secondTabResults.value.length) {
            this.secondTabSelected = false;
          }
          this.maxResultSize = !this.secondTabSelected
            ? this.currentResults.value.length
            : this.secondTabResults.value.length;

          if (results.length) {
            this.resultAwareService.captureHotelResults(results);
          }

          // Return the hotel ratings observable or empty observable
          if (!this.loadingHotelRatings && results && results.length) {
            const hotelsWithoutRatings = results.slice(0, size).filter((hotel) => {
              return !this.hotelRatings[hotel.name.toLowerCase()];
            });

            if (hotelsWithoutRatings.length) {
              this.loadingHotelRatings = true;
              const ratingsArgs: HotelRatingIdentifiers[] = hotelsWithoutRatings.map((hotel) => {
                return {
                  hotelGroupName: hotel.group,
                  hotelName: hotel.name
                };
              });
              return this.searchService.getMultipleHotelRatings(ratingsArgs).pipe(
                map((ratings) => ({ ratings, hotelsWithoutRatings })),
                finalize(() => (this.loadingHotelRatings = false))
              );
            }
          }
          return of({ ratings: null, hotelsWithoutRatings: [] });
        })
      ),
      ({ ratings, hotelsWithoutRatings }) => {
        if (ratings) {
          ratings.hotels.forEach((hotel) => {
            if (hotel && hotel.hotelName && !this.hotelRatings[hotel.hotelName.toLowerCase()]) {
              this.hotelRatings[hotel.hotelName.toLowerCase()] = new BehaviorSubject(hotel);
            }
          });

          // Add blank ratings for hotels that were requested but didn't come back with ratings
          hotelsWithoutRatings.forEach((hotel) => {
            if (hotel && hotel.name && !this.hotelRatings[hotel.name.toLowerCase()]) {
              this.hotelRatings[hotel.name.toLowerCase()] = new BehaviorSubject(
                {} as HotelRatingInfoAndTags
              );
            }
          });

          ratings.hotelGroups.forEach((hotelGroup) => {
            hotelGroup.matchAgainstGroupName.forEach((hotelGroupName) => {
              if (hotelGroupName && !this.hotelGroupTags[hotelGroupName.toLowerCase()]) {
                this.hotelGroupTags[hotelGroupName.toLowerCase()] = new BehaviorSubject(hotelGroup);
              }
            });
          });
        }
      }
    );

    this.filterResults();
    this.setResultsTitle('Hotel results');
    this.filtersSubscription();

    this.subscribe(
      combineLatest([this.loadingScreenInview, this.hotelAvalibilityService.fetching]),
      ([inview, fetching]) => {
        // console.log(`+++ Loading Screen In View: ${inview} | Fetching: ${fetching} +++`);
        if (inview && !fetching) {
          this.updateHotelResults(inview);
        }
      }
    );

    this.view =
      (this.userService
        .getUserFavoriteObject<IHotelResultsConfiguration>(
          LightningUserFavorurite.HotelResultsFormConfiguration
        )
        ?.hotelSearchView?.hotelSearchView.toLowerCase() as HotelView) || HotelView.LIST;

    this.setView(this.view);
  }

  ngAfterViewInit(): void {
    // Subscribe here so the hotel name filter is applied AFTER filter-hotels.component
    // initialises and calls clearFilters/resetFilters (which would wipe any filter set
    // in ngOnInit).

    // When a new search starts, clear any chatbot hotel name filter from the URL and search params.
    this.subscribe(this.searchParams.isLoading, (loading: boolean) => {
      if (loading) {
        if (this.jitFilterSub) {
          this.jitFilterSub.unsubscribe();
          this.jitFilterSub = null;
        }
        this.searchParams.filters.hotelName = '';
        this.router.navigate([], {
          queryParams: { hotelNameFilter: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });

    this.subscribe(this.activatedRoute.queryParams, (params) => {
      const hotelNameFilter = params['hotelNameFilter'] ?? '';
      if (hotelNameFilter) {
        this.searchParams.filters.hotelName = hotelNameFilter;
        this.searchParams.filterLighteningResults();

        // JIT rules merge availability into results.value without re-filtering,
        // so re-apply the hotel name filter each time JIT rules complete.
        if (this.jitFilterSub) {
          this.jitFilterSub.unsubscribe();
        }
        this.jitFilterSub = this.subscribe(
          this.searchParams.jitRulesReceived,
          (received: boolean) => {
            if (received) {
              this.searchParams.filters.hotelName = hotelNameFilter;
              this.searchParams.filterLighteningResults();
            }
          }
        );
      } else {
        if (this.jitFilterSub) {
          this.jitFilterSub.unsubscribe();
          this.jitFilterSub = null;
        }
        this.searchParams.filters.hotelName = '';
        this.searchParams.filterLighteningResults();
      }
    });
  }

  updateHotelResults(inview: boolean) {
    if (this.loadingScreenInview.value !== inview) {
      // recursion prevention
      this.loadingScreenInview.next(inview);
    }

    if (
      inview &&
      !this.hotelAvalibilityService.fetching.value &&
      this.viewPortSize.value < this.maxResultSize
    ) {
      console.log('Setting viewport to ', this.viewPortSize.value + 5);
      this.viewPortSize.next(this.viewPortSize.value + 5);
    }
    this.searchParams.hotelRoomReceived.next(true);
  }

  getCodeByHolidayCode(holidayCode): string {
    return holidayCode.substring(0, holidayCode.indexOf(','));
  }

  scrollToElement(f): void {
    const x = document.getElementById(f);
    x.scrollIntoView();
  }

  toggleFilterOptions(): void {
    // this.hotelViewController.showFilterOptions = !this.hotelViewController.showFilterOptions;
    this.showSortOptions = false;
    setTimeout(() => {
      this.readyToClose = !this.readyToClose;
    });
    this.ariaToggleHideBackground();
  }

  closeFilterOptions(): void {
    if (this.readyToClose) {
      // this.hotelViewController.showFilterOptions = false;
      this.readyToClose = false;
    }
    this.ariaToggleHideBackground();
  }

  closeValidationErrors(): void {
    this.show_errors = false;
  }

  getNumHotelsMsg(): string {
    if (this.hotelAvalibilityService.fetching && !this.searchParams.results?.value?.length) {
      return '';
    }

    if (!this.searchParams.results?.value?.length && !this.searchParams.crownRatesOnly) {
      return this.translateService.instant('No Results');
    } else if (!this.searchParams.results?.value?.length && this.searchParams.crownRatesOnly) {
      return null;
    } else {
      const numberResults = this.availabilityFilterState
        ? this.hotelAvalibilityService.numberOfHotelsWithPrice()
        : this.searchParams?.results?.value?.length;
      return this.translateService.instant(`Showing Hotels`, { num: numberResults });
    }
  }

  changeFilter(value: string[]) {
    if (!value) {
      this.clearFilters();
      return;
    }
    this.swapSellHotelGroup = value;
  }

  filterResults(): void {
    this.searchParams.filterLighteningResults();
  }

  setAvailabilityFilterState(state: boolean): void {
    this.availabilityFilterState = state;
  }

  setView(view: HotelView, hotelChosen = false): void {
    this.view = view;
    if (view === HotelView.MAP) {
      this.hotelViewController.hotelChosen = hotelChosen;
      this.hotelViewController.showMap = true;
      this.hotelViewController.single = false;
      if (hotelChosen === false) {
        this.updateHotels(this.getAllHotels());
      } else {
        this.updateHotels(this.convertHotelsToIds([this.filteredSearchResults[+hotelChosen]]));
      }
    } else {
      this.hotelViewController.hotelChosen = false;
      this.hotelViewController.showMap = false;
    }
  }

  getLoadingMessage(): string {
    if (
      this.searchParams.location_type_select === 'Airport' &&
      this.searchParams.location &&
      this.searchParams.location.name
    ) {
      return `${this.translateService.instant('Hotels near')} ${this.searchParams.location.name}`;
    } else if (this.searchParams.location_type_select === 'City') {
      if (this.searchParams.location && this.searchParams.location.name) {
        return `${this.translateService.instant('Hotels near')} ${this.searchParams.location.name}`;
      } else if (this.searchParams.postcode && this.searchParams.postcode.name) {
        return `${this.translateService.instant('Hotels near')} ${this.searchParams.postcode.name}`;
      } else {
        return `${this.translateService.instant('Hotels near your chosen')} ${this.searchParams.location_type_select}`;
      }
    } else {
      return `${this.translateService.instant('Hotels near your chosen')} ${
        this.searchParams.location_type_select
      }`;
    }
  }

  async updateVisibleHotels() {
    this.updateHotels(await this.getVisibleHotels());
  }

  private updateHotels(hotels: string[]): void {}

  /**
   * Gets Visible and never updated hotels
   */
  private getVisibleHotels(): Promise<string[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(
          this.hotels
            .filter((hotel) => {
              const bounding = hotel.nativeElement.getBoundingClientRect();
              const wasUpdated = this.updatedHotelIds.includes(hotel.nativeElement.id);
              return bounding.top >= 0 && bounding.top <= window.innerHeight && !wasUpdated;
            })
            .map((item) => {
              return item.nativeElement.id;
            })
        );
      }, this.delayValue);
    });
  }

  private getAllHotels(): string[] {
    return this.hotels?.map((item) => {
      return item.nativeElement.id;
    });
  }

  private convertHotelsToIds(hotels: HotelQuoteResult[]): string[] {
    return hotels.map((hotel) => {
      return this.getCodeByHolidayCode(hotel.id);
    });
  }

  isPrefHotelsSelected(): boolean {
    // return this.filterHotelsService.getPreferredHotelsSelected();
    return false;
  }

  backToHotelModal() {
    this.filterResults();
  }

  skipToResultContent(): boolean {
    return super.skipToResultContent('hotel-search-1');
  }

  switchTriggerPref(option: string) {}

  ariaToggleHideBackground(): void {
    super.ariaToggleHideBackground(
      'hotel-filters-wrapper',
      this.hotelViewController.showFilterOptions
    );
  }

  clearFilters() {
    const clearButton = document.getElementById('hotel-filters-clear');
    if (clearButton) {
      clearButton.click();
    }
  }

  fetchPrices(inView: boolean, hotel: HotelQuoteResult) {
    if (this.searchParams.isOriginalResults && hotel?.id) {
      this.hotelAvalibility[hotel.id] = this.hotelAvalibilityService.getAvailabilityForId(
        this.searchParams.makeHotelAvalilityObject(hotel),
        hotel
      );
      return;
    }

    if (!this.searchService.search_objects[ServiceType.Hotel]?.booking_chosen) {
      this.searchService.search_objects[ServiceType.Hotel].chosen = true;
    }
    if (inView && hotel?.id && !this.hotelAvalibility[hotel.id]) {
      this.hotelAvalibility[hotel.id] = this.hotelAvalibilityService.getAvailabilityForId(
        this.searchParams.makeHotelAvalilityObject(hotel),
        hotel
      );
    }
  }

  onSortChange(sortType: SortType) {
    sortType.methodRef();
    this.sortType = sortType;
  }

  performSearch() {
    if (window.location.href.includes('wellbeing')) {
      localStorage.setItem('prevURL', window.location.href); // prevent going back to wellbeing which will no longer have correct results
    }
    this.clearFilters();
    this.hotelRatings = {};
    this.hotelAvalibility = {};
    this.searchService.determineHighestSearchPriority();
    this.noOfRooms = this.searchParams.no_of_rooms;
    this.searchService.startSearches();
  }

  // #region ======= Private Functions ========
  private filtersSubscription() {
    this.subscribe(this.searchService.searches[ServiceType.Hotel].filterChange, () => {
      this.onFilterChange();
    });
  }

  /**
   * Event handler for filter change detection
   *
   * @memberof HotelListComponent
   */
  private onFilterChange(currentPageFromDetails = null) {
    this.filterResults();
  }

  public getRoomDetail(hotel: HotelDetails) {
    const hotelAvalibility: HotelAvalibilityQuoteResult =
      this.hotelAvalibilityService.getAvailabilityForId(
        this.searchParams.makeHotelAvalilityObject(hotel),
        hotel
      ).value;
    this.noOfRooms = hotelAvalibility?.rooms?.length;
    return hotelAvalibility;
  }

  selectedHotelOnTheMap(id: string) {
    return this.searchParams.results.value.find((hotel: HotelDetails) => hotel.id === id);
  }

  getCountryCode() {
    switch (this.searchParams?.location_type_select) {
      case LocationTypes.City:
      case LocationTypes.Shortlist:
        return this.searchParams.country?.cCode || '';
      case LocationTypes.Airport:
        return this.searchParams.location?.airport?.countryCode || '';
      case LocationTypes.Office:
        return this.searchParams.office?.countryCode || '';
      case LocationTypes.TrainStation:
        return 'GB';
      default:
        return '';
    }
  }

  ngOnDestroy(): void {
    this.hotelAvalibilityService.stopFetching();
    super.ngOnDestroy();
  }
}
